/**
 * Runtime configuration.
 *
 * One static bundle serves every environment: instead of baking env vars into
 * the build, we fetch `/config.json` once at boot (before the app renders).
 * This replaces the `NEXT_PUBLIC_*` vars used by the Next.js app.
 */

export interface AppConfig {
  /**
   * Origin the SPA talks to for the API (e.g. `https://api.example.com`), no
   * trailing slash and no `/v1` suffix. Empty = same-origin relative `/v1/*`.
   * Injected per-environment at container start, so one built image serves every
   * environment. Read via `@/lib/apiBase`, never `import.meta.env` directly.
   */
  apiUrl?: string;
  /** Resource the API Access Manager tab operates on (env override). */
  apiAccessManagerResource?: string;
  ereborDepositAccountTemplateId?: string;
  ereborProgramId?: string;
}

const DEFAULT_CONFIG: AppConfig = {};

let config: AppConfig | null = null;

/**
 * Fetch and cache `/config.json`. Resolves before the app renders so
 * `getConfig()` is always populated. On failure, falls back to empty defaults
 * rather than blocking boot. (The API origin is the `apiUrl` key, read via
 * `@/lib/apiBase`.)
 */
export async function loadConfig(): Promise<AppConfig> {
  if (config) return config;

  try {
    const res = await fetch("/config.json", {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new Error(`Failed to load config.json: ${res.status}`);
    }
    const parsed = (await res.json()) as AppConfig;
    config = { ...DEFAULT_CONFIG, ...parsed };
  } catch (error) {
    console.error("Could not load runtime config, using defaults.", error);
    config = { ...DEFAULT_CONFIG };
  }

  return config;
}

/**
 * Synchronous accessor for the already-loaded config. Throws if called before
 * `loadConfig()` has resolved (a programming error — boot order is guaranteed
 * by `main.tsx`).
 */
export function getConfig(): AppConfig {
  if (!config) {
    throw new Error("getConfig() called before loadConfig() resolved.");
  }
  return config;
}
